/* eslint-disable max-lines -- the create adapter plus the composer ports both chat and Home bind; this root refuses a second module. */

import { toast } from '@heidi/design-system/toast';
import { rpaBridge } from '@heidi/plugin-rpa';
import type { NavigatePort } from '@heidi/runtime/shell';
import { normalizePastedFile, SM } from '@heidi/utils';
import type { Editor } from '@tiptap/react';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  memo,
  useCallback,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type ReactNode,
  type Ref,
} from 'react';
import { useIntl } from 'react-intl';

import type { LinkedContextItem, WorkSurface } from '../../../contracts';
import {
  DEFAULT_WORK_CHAT_APPROVAL_MODE,
  linkedContextItemLabel,
  linkedContextItemSubtitle,
} from '../../../contracts';
import {
  composerDraftStorageKey,
  composerLinkedItemsAtom,
  PROJECT_CHAT_COMPOSER_PLACEHOLDER_VALUES,
  projectChatComposerMessages,
  PROJECTS_COMPOSER_MAX_STAGED_FILES,
  setComposerLinkedItemAtom,
  useChatAttachmentUploadAdapter,
  useComposerDraftPersistence,
  useWorkChatApprovalModePreference,
  useWorkRunSpeedPreference,
  type ChatAttachmentUploadPorts,
  type ProjectsComposerPromptDraft,
  type ProjectsComposerSkillReference,
} from '../../../data';
import {
  Composer,
  useWorkComposerSourcesSlot,
  type ComposerAddMenuBind,
  type ComposerDictateSlotBind,
  type ComposerDictationBind,
  type ComposerGlow,
  type ComposerHost,
  type ComposerPromptEditorBind,
  type ComposerProps,
  type WorkComposerSessionAppearance,
  type WorkCreateCommand,
  type WorkCreateSubmit,
} from '../../composer/Composer';
import {
  composerUploadedAttachments,
  type ComposerAttachments,
} from '../../composer/composer-attachments';
import {
  ComposerDictationBar,
  type ComposerDictationPorts,
} from '../../composer/composer-dictation-bar';
import { useStagedAttachments } from '../../composer/useStagedAttachments';
import {
  useWorkCreateSubmit,
  type WorkCreateSubmitOptions,
} from '../../composer/useWorkCreateSubmit';
import { Heidi2EvidenceTourAddMenuStep } from '../../heidi-2/evidence-tour/heidi-2-evidence-tour';
import { useDesktopTargetViewSelection } from '../../work-home/use-desktop-target-view-selection';
import { getWorkHostPort } from '../../work-home/work-home-ports';
import { useAvailableWorkflowSkillsWithWorkflows } from '../hooks/useAvailableWorkflowSkills';
import {
  useLinkableContextItems,
  type UseLinkableContextItemsArgs,
} from '../hooks/useLinkableContextItems';
import { mergePickedFiles, PROJECT_ATTACHMENTS_ACCEPT_FORMATS } from '../lib/attachments';
import { DesktopTargetSelector } from './DesktopTargetSelector';
import {
  LINK_CONTEXT_PICKER_LIMIT,
  LinkContextButtonView as LinkContextButton,
} from './LinkContextButton';
import {
  ProjectsComposerPromptEditor,
  type ProjectsComposerPromptEditorProps,
} from './ProjectsComposerPromptEditor';
import type { WorkHomeSceneProps } from './WorkHomeScene';
import type { ProjectsComposerPromptEditorHandle } from './WorkSessionComposer.host';

export type ProjectsComposerSessionAppearance = WorkComposerSessionAppearance;

type WorkCreateComposerHost = Extract<
  ComposerHost,
  { kind: 'explore' } | { kind: 'routine' }
>;

export interface ProjectsLandingComposerProps {
  host?: WorkCreateComposerHost;
  onSubmit?: (command: WorkCreateCommand) => void;
  isSubmitting?: boolean;
  disabled?: boolean;
  className?: string;
  showRunOptions?: boolean;
  showSpeed?: boolean;
  placeholder?: string;
  appearance?: 'default' | 'flat';
  showVoiceTrigger?: boolean;
  glow?: ComposerGlow;
  disabledMessage?: string;
  sendDisabled?: boolean;
  composerDockLeading?: ReactNode;
  toolbarSourcesSlot?: ReactNode;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

const EXPLORE_COMPOSER_HOST: WorkCreateComposerHost = { kind: 'explore' };

const noop = (): void => undefined;

interface ComposerPromptEditorPortOptions {
  includeCreateWorkflow?: boolean;
  includeRecordAction?: boolean;
  slashSectionOrder?: ProjectsComposerPromptEditorProps['slashSectionOrder'];
  /** Where the editor's own slash picker opens against the composer card. */
  pickerPlacement?: 'above' | 'below';
  /** Submit the editor's live draft rather than the composer's settled one. */
  submitsEditorDraft?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
  prefilledValue?: string;
  placeholderPreview?: string | null;
  analyticsSurface?: WorkSurface;
  className?: string;
  editorClassName?: string;
  onLinkContext?: (item: LinkedContextItem | null) => void;
  onUnhandledKeyDown?: (event: KeyboardEvent) => boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  onClipboardPaste?: (event: ClipboardEvent) => boolean;
  onEditorChange?: (editor: Editor | null) => void;
  onPickerOpenChange?: (open: boolean) => void;
  patientScope?: UseLinkableContextItemsArgs['patientScope'];
}

/** Outer factory is pure — no getWorkHostPort. Inner component reads ports at render. */
const composerPromptEditorPort = (options: ComposerPromptEditorPortOptions = {}) =>
  function ComposerPromptEditorPort(bind: ComposerPromptEditorBind): ReactNode {
    return (
      <ProjectsComposerPromptEditor
        ref={bind.editorRef as Ref<ProjectsComposerPromptEditorHandle>}
        pickerAnchorRef={bind.anchorRef}
        pickerPlacement={options.pickerPlacement}
        availableSkills={bind.availableSkills}
        autoFocus={options.autoFocus ?? bind.autoFocus}
        placeholder={bind.placeholder}
        placeholderPreview={options.placeholderPreview ?? bind.placeholderPreview}
        placeholderOverlay={bind.placeholderOverlay}
        disabled={options.disabled ?? bind.disabled}
        prefilledValue={options.prefilledValue ?? bind.prefilledValue}
        prefilledSkill={bind.prefilledSkill}
        onDraftChange={bind.onDraftChange}
        onSubmitRequest={(draft) =>
          bind.onSubmitRequest(options.submitsEditorDraft ? draft : undefined)
        }
        onShortcutSend={bind.onShortcutSend}
        onUnhandledKeyDown={options.onUnhandledKeyDown}
        onLinkContext={(item) => (options.onLinkContext ?? bind.onLinkContext)(item)}
        onOpenArtifact={(artifact) => bind.onOpenArtifact(artifact)}
        objectTokens={bind.objectTokens}
        onFocus={options.onFocus}
        onBlur={options.onBlur}
        onClipboardPaste={
          options.onClipboardPaste ??
          (bind.onClipboardPaste
            ? (event) => {
                bind.onClipboardPaste?.(event);
                return event.defaultPrevented;
              }
            : undefined)
        }
        onEditorChange={(editor) => {
          options.onEditorChange?.(editor);
          bind.onEditorChange(editor);
        }}
        onPickerOpenChange={options.onPickerOpenChange}
        includeCreateWorkflow={options.includeCreateWorkflow}
        includeRecordAction={options.includeRecordAction}
        slashSectionOrder={options.slashSectionOrder}
        analyticsSurface={options.analyticsSurface ?? bind.analyticsSurface}
        patientScope={options.patientScope}
        className={options.className ?? bind.className}
        editorClassName={options.editorClassName ?? bind.editorClassName}
        testId={bind.testId}
      />
    );
  };

interface ComposerAddMenuModeRowSpotlight {
  open: boolean;
  wrap: (row: ReactNode) => ReactNode;
  onModeRowToggle?: () => void;
}

interface ComposerAddMenuPortOptions {
  onSet?: (item: LinkedContextItem | null) => void;
  onAfterPick?: () => void;
  skillsMenu?: ComposerAddMenuBind['skillsMenu'];
  speedMenu?: ComposerAddMenuBind['speedMenu'];
  modeMenu?: ComposerAddMenuBind['modeMenu'];
  disabled?: boolean;
  hideLinkedContextPickers?: boolean;
  hidePatientPicker?: boolean;
  patientScope?: UseLinkableContextItemsArgs['patientScope'];
  analyticsSurface?: WorkSurface;
  /**
   * Work-home Evidence tour over the Evidence-only row. Resolved here so the
   * Chat page does not import the tour or the on-prem build flag.
   */
  evidenceTour?: boolean;
  renderShortcutsFlyout?: (actions: {
    onSend: (query: string) => void;
    close: () => void;
  }) => ReactNode;
}

/**
 * Outer factory is pure. Composer calls `renderAddMenu(bind)` as a plain function
 * inside conditional branches, so the port must return an element and keep its
 * hooks in ComposerAddMenu, never in the port body itself.
 */
const composerAddMenuPort = (options: ComposerAddMenuPortOptions = {}) =>
  function ComposerAddMenuPort(bind: ComposerAddMenuBind): ReactNode {
    return <ComposerAddMenu bind={bind} options={options} />;
  };

function ComposerAddMenu({
  bind,
  options,
}: {
  bind: ComposerAddMenuBind;
  options: ComposerAddMenuPortOptions;
}): ReactNode {
  const hostFlyout = options.renderShortcutsFlyout;
  const onShortcutSend = bind.onShortcutSend;
  const shell = getWorkHostPort('shell');
  const WorkSourcesPopover = getWorkHostPort('bridge').WorkSourcesPopover;
  // Host owns the derivation call so LinkContextButton stays a pure picker.
  const pickerItems = useLinkableContextItems({
    limit: LINK_CONTEXT_PICKER_LIMIT,
    patientScope: options.patientScope,
  });
  const button = (modeRowSpotlight?: ComposerAddMenuModeRowSpotlight) => (
    <LinkContextButton
      {...({
        items: bind.items,
        onSet: options.onSet ?? bind.onSet,
        onPickObject: bind.onPickObject,
        onAfterPick: options.onAfterPick ?? bind.onAfterPick,
        onAttachFiles: bind.onAttachFiles,
        skillsMenu: options.skillsMenu ?? bind.skillsMenu,
        speedMenu: options.speedMenu ?? bind.speedMenu,
        modeMenu: options.modeMenu ?? bind.modeMenu,
        modeRowSpotlight,
        hideLinkedContextPickers: options.hideLinkedContextPickers,
        hidePatientPicker: options.hidePatientPicker,
        // Passed through for in-session hosts / suite observability; the core
        // menu reads pickerItems and does not declare this prop.
        patientScope: options.patientScope,
        pickerItems,
        disabled: options.disabled ?? bind.disabled,
        analyticsSurface: options.analyticsSurface ?? bind.analyticsSurface,
        showSourcesInAddMenu: bind.showSourcesInAddMenu,
        onTrackCta: shell.trackWorkHomeComposerCta,
        onTrackViewed: shell.trackWorkHomeComposerViewed,
        onTrackRoutineActioned: shell.trackWorkHomeComposerRoutineActioned,
        SourcesPopover: WorkSourcesPopover,
        renderShortcutsFlyout:
          hostFlyout && onShortcutSend
            ? (close: () => void) => hostFlyout({ onSend: onShortcutSend, close })
            : undefined,
      } as unknown as ComponentProps<typeof LinkContextButton>)}
    />
  );

  if (!options.evidenceTour || shell.isOnPrem) return button();

  return (
    <Heidi2EvidenceTourAddMenuStep>
      {(spotlight) => button(spotlight)}
    </Heidi2EvidenceTourAddMenuStep>
  );
}

/** Outer factory is pure — ComposerDictateSlot is read when React renders the inner port. */
const composerDictateSlotPort = (options: { disabled?: boolean } = {}) =>
  function ComposerDictateSlotPort(bind: ComposerDictateSlotBind): ReactNode {
    const ComposerDictateSlot = getWorkHostPort('shell').ComposerDictateSlot;
    return (
      <ComposerDictateSlot
        editor={bind.editor}
        getEditor={bind.getEditor}
        disabled={options.disabled ?? bind.disabled}
        variant="ghost"
        size="md"
        buttonClassName={bind.buttonClassName}
        onDictateClick={bind.onDictateClick}
      />
    );
  };

/**
 * Lazy cache for the app half of the package's live-dictation row. Built on first
 * render of the inner DictationBar so module evaluation never calls getWorkHostPort.
 * Object identity stays stable after the first render.
 *
 * `useAudioAnalyzer` is a desktop port. Capture builds its AudioContext inside
 * init(), so this file never imports that door.
 */
let cachedComposerDictationPorts: ComposerDictationPorts | null = null;
let cachedChatAttachmentUploadPorts: ChatAttachmentUploadPorts | null = null;

/** Clears the lazy dictation/upload port caches so suite doubles can rebind. */
export const resetComposerHostPortCachesForTests = (): void => {
  cachedComposerDictationPorts = null;
  cachedChatAttachmentUploadPorts = null;
};

const getComposerDictationPorts = (): ComposerDictationPorts => {
  if (!cachedComposerDictationPorts) {
    const desktop = getWorkHostPort('desktop');
    const session = getWorkHostPort('session');
    /* eslint-disable @typescript-eslint/unbound-method -- port members are plain functions kept by identity */
    cachedComposerDictationPorts = {
      useDictationStore: session.useDictationStore,
      // `isTauri` (the desktop host) and `shouldUseNativeAudio` (native capture)
      // are different predicates; the row needs both.
      isNativeHost: desktop.isTauri,
      stopNativeDictation: desktop.stopNativeDictation,
      cancelNativeDictation: desktop.cancelNativeDictation,
      shouldUseNativeAudio: desktop.shouldUseNativeAudio,
      useReadyToRequestMic: desktop.useReadyToRequestMicPermissions,
      useAudioAnalyzer: desktop.useAudioAnalyzer,
      readNativeAudioLevels: desktop.readTauriAudioLevels,
      useAudioLevelsSubscription: desktop.useAudioLevelsSubscription,
      getBarHeights: desktop.getBarHeights,
    };
    /* eslint-enable @typescript-eslint/unbound-method */
  }
  return cachedComposerDictationPorts;
};

/** Outer factory is pure. Ports object is built lazily on first inner render. */
const composerDictationBarPort = () =>
  function ComposerDictationBarPort(bind: ComposerDictationBind): ReactNode {
    return (
      <ComposerDictationBar
        editor={bind.editor}
        getEditor={bind.getEditor}
        onActiveChange={bind.onActiveChange}
        ports={getComposerDictationPorts()}
      />
    );
  };

const CREATE_PROMPT_EDITOR = composerPromptEditorPort({
  includeRecordAction: true,
  pickerPlacement: 'below',
});
const CREATE_ADD_MENU = composerAddMenuPort();
const CREATE_DICTATE_SLOT = composerDictateSlotPort();
const CREATE_DICTATION_BAR = composerDictationBarPort();

/**
 * Voice trigger wrapper whose BODY calls getWorkHostPort at render — never assigned
 * as `VoiceTrigger: getWorkHostPort(...).VoiceAssistantTrigger` at module scope.
 */
function ProjectsLandingVoiceTrigger(props: Record<string, unknown>) {
  const VoiceAssistantTrigger = getWorkHostPort('routines').VoiceAssistantTrigger;
  return <VoiceAssistantTrigger {...props} />;
}

/**
 * The app's half of a routine launch from the composer: the routine run
 * mutation and the route push into the run it started. The launch lock and the
 * create routing are the package's. A Run by pick rides along for that run only.
 */
const useRoutineLaunchPorts = () => {
  const navigate = getWorkHostPort('shell').useNavigate();
  const { mutate } = getWorkHostPort('routines').useRunAnyWorkflow();
  const desktopTargetOverride = useDesktopTargetViewSelection()?.override;
  const onNavigate = useCallback<NavigatePort>(
    async (href) => {
      await navigate(href);
    },
    [navigate]
  );
  const runRoutine = useCallback<WorkCreateSubmitOptions['runRoutine']>(
    (run, callbacks) =>
      mutate(desktopTargetOverride ? { ...run, desktopTargetOverride } : run, callbacks),
    [desktopTargetOverride, mutate]
  );
  return { runRoutine, onNavigate };
};

/** Chat's attachment upload stack, which the Work-home upload adapter wraps. */
const getChatAttachmentUploadPorts = (): ChatAttachmentUploadPorts => {
  if (!cachedChatAttachmentUploadPorts) {
    const evidence = getWorkHostPort('evidence');
    /* eslint-disable @typescript-eslint/unbound-method -- port members are plain functions kept by identity */
    cachedChatAttachmentUploadPorts = {
      useUploadAttachment: evidence.useMutateUploadChatAttachment,
      useDeleteAttachment: evidence.useMutateDeleteChatAttachment,
      cancelUpload: evidence.cancelChatAttachmentUpload,
    };
    /* eslint-enable @typescript-eslint/unbound-method */
  }
  return cachedChatAttachmentUploadPorts;
};

/**
 * Shared by Discover and Agent Home. Chat attachments pre-upload through
 * `POST /chat/attachments` so a later Evidence redirect can carry the ids.
 * With the flag off, files stay staged in the browser.
 */
const useLandingComposerAttachments = (): ComposerAttachments => {
  const intl = useIntl();
  const evidence = getWorkHostPort('evidence');
  const preUploadEnabled = Boolean(getWorkHostPort('shell').isChatAttachmentsEnabled());
  const uploadAdapter = useChatAttachmentUploadAdapter(getChatAttachmentUploadPorts());
  const {
    files: preUploadedFiles,
    addFiles: addPreUploadedFiles,
    removeFile: removePreUploadedFile,
    retryUpload: retryPreUploadedFile,
  } = evidence.useChatFileUpload({
    adapter: uploadAdapter,
    enableUpload: preUploadEnabled,
    formatErrorMessage: (errorCode) =>
      evidence.formatChatUploadErrorMessage(intl, errorCode),
  });
  const stagedAttachments = useStagedAttachments({
    mergePickedFiles,
    accept: PROJECT_ATTACHMENTS_ACCEPT_FORMATS,
  });
  const { items: uploadedItems, readyToSend: uploadsReadyToSend } = useMemo(
    () => composerUploadedAttachments(preUploadedFiles),
    [preUploadedFiles]
  );
  const ImageFileCard = evidence.ImageFileCard;
  const uploadedAttachments = useMemo<ComposerAttachments>(
    () => ({
      items: uploadedItems,
      attach: (files) => {
        const existing = preUploadedFiles.map((file) => file.file);
        const { next, error } = mergePickedFiles(existing, files, {
          maxCount: PROJECTS_COMPOSER_MAX_STAGED_FILES,
          oversizedPolicy: 'skip',
        });
        const added = next.slice(existing.length);
        if (error && added.length === 0) toast({ ...error, tone: 'destructive' });
        if (added.length > 0) addPreUploadedFiles(added);
      },
      remove: (id) => removePreUploadedFile(id),
      retry: (id) => retryPreUploadedFile(id),
      readyToSend: uploadsReadyToSend,
      accept: PROJECT_ATTACHMENTS_ACCEPT_FORMATS,
      renderImageItem: (item, index) => {
        const file = preUploadedFiles[index];
        if (!file) return null;
        return (
          <ImageFileCard
            file={file.file}
            uploadStatus={file.uploadStatus}
            uploadProgress={item.progress}
            previewUrl={getWorkHostPort('evidence').getPreviewUrl(file)}
            errorMessage={item.error}
            onDelete={() => removePreUploadedFile(file.uid)}
            onRetry={
              item.status === 'error' ? () => retryPreUploadedFile(file.uid) : undefined
            }
          />
        );
      },
    }),
    [
      ImageFileCard,
      addPreUploadedFiles,
      preUploadedFiles,
      removePreUploadedFile,
      retryPreUploadedFile,
      uploadedItems,
      uploadsReadyToSend,
    ]
  );

  return preUploadEnabled ? uploadedAttachments : stagedAttachments;
};

interface WorkHomeComposerWiringBind {
  availableSkills: readonly ProjectsComposerSkillReference[];
  isDesktopBridgeAvailable: boolean;
  onSubmit: WorkCreateSubmit;
  mergePickedFiles: typeof mergePickedFiles;
  attachmentAccept: string;
  normalizeDroppedAttachmentFile: WorkHomeSceneProps['normalizeDroppedAttachmentFile'];
  formatDroppedAttachmentRejection: WorkHomeSceneProps['formatDroppedAttachmentRejection'];
  attachments: ComposerAttachments;
  onClipboardPaste: NonNullable<ComposerProps['onClipboardPaste']>;
}

/**
 * Resolves the app-side composer ports (routine pathway, attachment policy,
 * clipboard paste) outside the Chat-quarantined Home route, which cannot reach
 * these hooks and modules itself.
 */
const WorkHomeComposerWiring = ({
  onCreate,
  isSubmitting = false,
  sendDisabled = false,
  children,
}: {
  onCreate: (command: WorkCreateCommand) => void;
  isSubmitting?: boolean;
  sendDisabled?: boolean;
  children: (bind: WorkHomeComposerWiringBind) => ReactNode;
}): JSX.Element => {
  const isDesktopBridgeAvailable = rpaBridge.isAvailable();
  const workflowSkills = useAvailableWorkflowSkillsWithWorkflows();
  const availableSkills = useMemo(
    () =>
      isDesktopBridgeAvailable
        ? workflowSkills
        : workflowSkills.filter((skill) => skill.kind === 'workflow'),
    [isDesktopBridgeAvailable, workflowSkills]
  );

  // The composer hands its own attach callback in per paste, so the hook routes
  // through a ref rather than closing over one.
  const attachPastedFilesRef =
    useRef<(files: FileList | readonly File[] | null) => void>();
  const { handlePaste } = getWorkHostPort('evidence').useChatPaste({
    onFilesDetected: (files) => attachPastedFilesRef.current?.(files),
  });
  const onClipboardPaste = useCallback<WorkHomeComposerWiringBind['onClipboardPaste']>(
    (event, attachFiles) => {
      attachPastedFilesRef.current = attachFiles;
      handlePaste(event);
    },
    [handlePaste]
  );

  const routineLaunch = useRoutineLaunchPorts();
  const onSubmit = useWorkCreateSubmit({
    availableSkills,
    disabled: false,
    sendDisabled,
    isSubmitting,
    onCreate,
    ...routineLaunch,
  });
  const attachments = useLandingComposerAttachments();

  return (
    <>
      {children({
        availableSkills,
        isDesktopBridgeAvailable,
        onSubmit,
        mergePickedFiles,
        attachmentAccept: PROJECT_ATTACHMENTS_ACCEPT_FORMATS,
        normalizeDroppedAttachmentFile: normalizePastedFile,
        formatDroppedAttachmentRejection: (rejections) =>
          mergePickedFiles([], [], { dropRejections: rejections }).error,
        attachments,
        onClipboardPaste,
      })}
    </>
  );
};

const ProjectsLandingComposerView = memo(function ProjectsLandingComposer({
  host = EXPLORE_COMPOSER_HOST,
  onSubmit,
  isSubmitting = false,
  disabled = false,
  className,
  showRunOptions = false,
  showSpeed = false,
  placeholder,
  appearance = 'default',
  showVoiceTrigger = true,
  glow = 'always',
  disabledMessage,
  sendDisabled = false,
  composerDockLeading,
  toolbarSourcesSlot,
  expanded = false,
  onExpandedChange,
}: ProjectsLandingComposerProps) {
  const intl = useIntl();
  const shell = getWorkHostPort('shell');
  const evidence = getWorkHostPort('evidence');
  const routines = getWorkHostPort('routines');
  const desktop = getWorkHostPort('desktop');
  const inheritedSourcesSlot = useWorkComposerSourcesSlot();
  const resolvedToolbarSourcesSlot = toolbarSourcesSlot ?? inheritedSourcesSlot;
  const editorRef = useRef<ProjectsComposerPromptEditorHandle | null>(null);
  const [draft, setDraft] = useState<ProjectsComposerPromptDraft>({
    text: '',
    segments: [],
  });
  const linkedItems = useAtomValue(composerLinkedItemsAtom);
  const setLinkedItem = useSetAtom(setComposerLinkedItemAtom);
  const chatObjectRefsEnabled = shell.useIsChatObjectRefsEnabled();

  const isDesktopBridgeAvailable = rpaBridge.isAvailable();
  const workflowSkills = useAvailableWorkflowSkillsWithWorkflows();
  const availableSkills = useMemo(
    () =>
      isDesktopBridgeAvailable
        ? workflowSkills
        : workflowSkills.filter((skill) => skill.kind === 'workflow'),
    [isDesktopBridgeAvailable, workflowSkills]
  );

  const { persistedApprovalMode, rememberApprovalMode } =
    useWorkChatApprovalModePreference();
  const { persistedSpeed, rememberSpeed } = useWorkRunSpeedPreference();
  const desktopTarget = desktop.useDesktopTargetSelection({ surface: 'landing' });

  const [workflowLaunchingId, setWorkflowLaunchingId] = useState<string | null>(null);

  const workflowBusy = workflowLaunchingId !== null;
  const sendBusy = isSubmitting || workflowBusy;

  const attachments = useLandingComposerAttachments();

  const { sessionItems, patientItems } = useLinkableContextItems({ limit: 50 });
  const libraryDocuments = routines.useReferenceableLibraryDocuments({ limit: 50 });
  const linkableItems = useMemo(
    () => [...sessionItems, ...patientItems, ...libraryDocuments],
    [libraryDocuments, patientItems, sessionItems]
  );
  const { clearPersistedDraft } = useComposerDraftPersistence({
    storageKey: composerDraftStorageKey('landing'),
    enabled: !disabled && !sendBusy,
    draft,
    linkedItems,
    linkableItems,
    editorRef,
    onRestoreLinkedItem: setLinkedItem,
    onDraftRestored: setDraft,
  });

  const attachPastedFilesRef =
    useRef<(files: FileList | readonly File[] | null) => void>();
  const { handlePaste } = evidence.useChatPaste({
    onFilesDetected: (files) => attachPastedFilesRef.current?.(files),
    enabled: !disabled && !sendBusy,
  });
  const onClipboardPaste = useCallback<NonNullable<ComposerProps['onClipboardPaste']>>(
    (event, attach) => {
      attachPastedFilesRef.current = attach;
      handlePaste(event);
    },
    [handlePaste]
  );

  const routineLaunch = useRoutineLaunchPorts();
  const handleSubmit = useWorkCreateSubmit({
    availableSkills,
    disabled,
    sendDisabled: sendDisabled || workflowBusy,
    isSubmitting: sendBusy,
    onCreate: onSubmit ?? noop,
    onCreated: clearPersistedDraft,
    onLaunchingChange: setWorkflowLaunchingId,
    ...routineLaunch,
  });

  const isCompactPlaceholder = shell.useMediaQuery(`(max-width: ${SM - 1}px)`);
  const resolvedPlaceholder =
    placeholder ??
    intl.formatMessage(
      isCompactPlaceholder
        ? projectChatComposerMessages.placeholderCompact
        : projectChatComposerMessages.placeholder,
      PROJECT_CHAT_COMPOSER_PLACEHOLDER_VALUES
    );

  const LinkedContextBadge = evidence.LinkedContextBadge;

  return (
    <>
      <Composer
        host={host}
        layout="full"
        className={className}
        attachments={attachments}
        onSubmit={handleSubmit}
        isSubmitting={sendBusy}
        disabled={disabled}
        disabledMessage={disabledMessage}
        elevated={false}
        glow={glow}
        showRunOptions={showRunOptions}
        showSpeed={showSpeed}
        runSpeed={persistedSpeed}
        onRunSpeedChange={rememberSpeed}
        chatObjectRefsEnabled={chatObjectRefsEnabled}
        placeholder={resolvedPlaceholder}
        draftValue={draft}
        onDraftChange={setDraft}
        onShortcutSubmit={(payload, keptDraft) =>
          handleSubmit(payload, (command) => ({ ...command, keptDraft }))
        }
        promptEditorRef={editorRef}
        availableSkills={availableSkills}
        isDesktopBridgeAvailable={isDesktopBridgeAvailable}
        onClipboardPaste={onClipboardPaste}
        approvalMode={persistedApprovalMode ?? DEFAULT_WORK_CHAT_APPROVAL_MODE}
        onApprovalModeChange={rememberApprovalMode}
        trackCta={shell.trackWorkHomeComposerCta}
        sendDisabled={sendDisabled}
        sendBlocked={workflowBusy}
        persistentSend={!showVoiceTrigger}
        voiceTrigger={showVoiceTrigger ? <ProjectsLandingVoiceTrigger /> : undefined}
        composerDockLeading={composerDockLeading}
        toolbarSourcesSlot={resolvedToolbarSourcesSlot}
        promptOverflow={{
          expanded,
          onToggle: onExpandedChange ? () => onExpandedChange(!expanded) : undefined,
        }}
        desktopTargetSlot={
          appearance !== 'flat' && desktopTarget.visible ? (
            <div className="flex w-full items-center ps-2">
              <DesktopTargetSelector
                desktopTarget={desktopTarget}
                disabled={disabled || isSubmitting}
              />
            </div>
          ) : null
        }
        linkedContextBadge={(item, bind) => (
          <LinkedContextBadge
            // The badge's prop union lives app-side (`src/components/ui`) and is
            // deliberately duplicated rather than imported from
            // `@heidi/agents/contracts` — the Chat quarantine forced it out of the
            // Chat tree. A template renders identically to a library document
            // (same FileOutput icon, no subtitle), so map rather than widen it.
            type={item.type === 'template' ? 'library_document' : item.type}
            name={linkedContextItemLabel(item)}
            subtitle={linkedContextItemSubtitle(item)}
            onClick={bind.onOpen}
            onRemove={bind.onRemove}
          />
        )}
        renderPromptEditor={CREATE_PROMPT_EDITOR}
        renderAddMenu={CREATE_ADD_MENU}
        dictateSlot={CREATE_DICTATE_SLOT}
        renderDictation={CREATE_DICTATION_BAR}
      />
    </>
  );
});

// Hung on the existing export so the Work page and the chat adapter can bind
// these ports without a new locked-root symbol or a new Chat-quarantine edge.
export const ProjectsLandingComposer = Object.assign(ProjectsLandingComposerView, {
  PromptEditor: composerPromptEditorPort,
  LinkContext: composerAddMenuPort,
  VoiceTrigger: ProjectsLandingVoiceTrigger,
  DictateSlot: composerDictateSlotPort,
  DictationBar: composerDictationBarPort,
  ComposerWiring: WorkHomeComposerWiring,
});
